import { Controller } from "@hotwired/stimulus"
const SIGN_IN_REQUIRED_MODAL_ID = "signInRequiredModal"
const LIMITED_PERMISSIONS_MODAL_ID = "limitedPermissionsModal"

export default class extends Controller {
  static targets = [
    "term", "startDate", "endDate", "form",
    "selectedSystemCard", "selectedDisciplineCard", "selectedTab", "selectedBodySystems",
    "selectedClinicalDisciplines", "selectedCourses", "selectedStudents", "filterPills",
    "saveReportName", "saveReportButton",
    "selectedCustomCourses", "selectedAssessmentDeliveries"
  ]

  connect() {
    this.loadCourses()
    this.initializeDatePickers()
    this.restoreNonCoursePills()
    const currentTab = this.selectedTabTarget.value
    this.applyTabFilterVisibility(currentTab)
    this.toggleSaveButton()
    document.addEventListener("students:updated", this.handleStudentsUpdated)
    document.addEventListener("turbo:frame-load", this.handleFrameLoad)
    this.syncTrExpandedClass()
  }

  disconnect() {
    document.removeEventListener("students:updated", this.handleStudentsUpdated);
    document.removeEventListener("turbo:frame-load", this.handleFrameLoad);
  }

  /* ----------------------- DATE PICKERS ----------------------- */

  defaultFirstDay() {
    return new Date(2025, 6, 1)
  }

  initializeDatePickers() {
    const FIRST_DAY = this.defaultFirstDay();
    const TODAY = new Date();

    const startDateOptions = {
      numberOfMonths: 1,
      dateFormat: "mm-dd-yy",
      onSelect: (dateText) => this.handleStartDateSelected(dateText)
    };

    const endDateOptions = {
      numberOfMonths: 1,
      dateFormat: "mm-dd-yy",
      onSelect: (dateText) => this.handleEndDateSelected(dateText)
    };

    // Always initialize datepickers so users can pick dates
    if (this.hasStartDateTarget) {
      $(this.startDateTarget).datepicker(startDateOptions);
      if (!this.startDateTarget.value) {
        this.startDateTarget.value = $.datepicker.formatDate("mm-dd-yy", FIRST_DAY);
      }
    }

    if (this.hasEndDateTarget) {
      $(this.endDateTarget).datepicker(endDateOptions);
      if (!this.endDateTarget.value) {
        this.endDateTarget.value = $.datepicker.formatDate("mm-dd-yy", TODAY);
      }
    }
  }

  handleStartDateSelected(dateText) {
    const startDate = $.datepicker.parseDate("mm-dd-yy", dateText);
    if (startDate) {
      this.endDateTarget.disabled = false;
      $(this.endDateTarget).datepicker("option", "minDate", startDate);

      if (!this.endDateTarget.value) {
        const today = new Date();
        const formattedToday = $.datepicker.formatDate("mm-dd-yy", today);
        this.endDateTarget.value = formattedToday;
      }
    } else {
      this.endDateTarget.value = "";
      this.endDateTarget.disabled = true;
    }

    this.submitForm();
  }

  handleEndDateSelected(dateText) {
    const endDate = $.datepicker.parseDate("mm-dd-yy", dateText);
    const startDate = $.datepicker.parseDate("mm-dd-yy", this.startDateTarget.value);

    if (!endDate || endDate < startDate) {
      $(this.endDateTarget).datepicker("setDate", startDate);
    }
    this.submitForm();
  }

  formatDate(event) {
    const target = event.target;
    const isStartDate = target === this.startDateTarget;

    const FIRST_DAY = this.defaultFirstDay();
    const TODAY = new Date();
    const defaultDate = isStartDate ? FIRST_DAY : TODAY;
    const dateFormat = "mm-dd-yy";

    let digits = target.value.replace(/\D/g, "");

    // Auto-insert dashes
    if (digits.length > 2 && digits.length <= 4) {
      target.value = `${digits.substring(0, 2)}-${digits.substring(2)}`;
    } else if (digits.length > 4) {
      target.value = `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4, 8)}`;
    } else {
      target.value = digits;
    }

    if (event.type === "change") {
      let isValid = false;
      let dateObj;

      if (digits.length === 8) {
        let month = digits.substring(0, 2);
        let day = digits.substring(2, 4);
        let year = parseInt(digits.substring(4, 8), 10);
        dateObj = new Date(`${year}-${month}-${day}`);

        if (
          !isNaN(dateObj.getTime()) &&
          month >= "01" && month <= "12" &&
          day >= "01" && day <= "31" &&
          year > 2000 && year < 2100
        ) {
          target.value = `${month}-${day}-${year}`;
          isValid = true;
        }
      }

      if (!isValid) {
        target.value = $.datepicker.formatDate(dateFormat, defaultDate);
        dateObj = defaultDate;
      }

      if (isStartDate) {
        // Enable end date on start date change
        this.endDateTarget.disabled = false;
        $(this.endDateTarget).datepicker("option", "minDate", dateObj);
        // If end date is empty, set to today's date
        if (!this.endDateTarget.value) {
          const today = new Date();
          const formattedToday = $.datepicker.formatDate("mm-dd-yy", today);
          this.endDateTarget.value = formattedToday;
        }
      } else {
        // Ensure end date >= start date
        const startDate = $.datepicker.parseDate(dateFormat, this.startDateTarget.value);
        if (dateObj < startDate) {
          target.value = $.datepicker.formatDate(dateFormat, startDate);
        }
      }

      $(target).datepicker("hide");
      target.blur();
      this.submitForm();
    }

    // If user is typing in start date, clear end date immediately
    if (isStartDate && event.type === "input") {
      this.endDateTarget.value = "";
      this.endDateTarget.disabled = true;
    }
  }

  handleDateEnter(event) {
    event.preventDefault();

    this.formatDate({
      target: event.target,
      type: "change"
    });
  }

  /* ----------------------- FORM SUBMIT ----------------------- */

  stripSavedReportIdFromForm() {
    if (!this.hasFormTarget) return
    const input = this.formTarget.querySelector('input[name="saved_report_id"]')
    if (input) input.remove()
  }

  submitForm() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      if (!this.hasFormTarget) return
      this.stripSavedReportIdFromForm()
      this.formTarget.requestSubmit()
    }, 150)
  }

  /* ----------------------- TOGGLE SELECTION ----------------------- */

  isGuestUser() {
    const v = this.element.dataset.reportFilterSignedInValue
    if (v === undefined) return false
    return v !== "true"
  }

  isLimitedAccessUser() {
    return this.element.dataset.reportFilterLimitedAccessValue === "true"
  }

  showSignInRequiredModal() {
    const modalEl = document.getElementById(SIGN_IN_REQUIRED_MODAL_ID)
    if (!modalEl) return
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modalEl).show()
      return
    }
    if (typeof $ !== "undefined" && typeof $.fn.modal !== "undefined") {
      $(modalEl).modal("show")
    }
  }

  showLimitedPermissionsModal() {
    const modalEl = document.getElementById(LIMITED_PERMISSIONS_MODAL_ID)
    if (!modalEl) return
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modalEl).show()
      return
    }
    if (typeof $ !== "undefined" && typeof $.fn.modal !== "undefined") {
      $(modalEl).modal("show")
    }
  }

  isPublicPracticeSmartFilterType(type) {
    return type === "clinical_disciplines" || type === "body_systems"
  }

  toggleSelection(event) {
    const cb = event.target
    const type = cb.dataset.filterType
    if (!this.isPublicPracticeSmartFilterType(type)) {
      if (this.isGuestUser()) {
        event.preventDefault()
        this.showSignInRequiredModal()
        return
      }
      if (this.isLimitedAccessUser()) {
        event.preventDefault()
        this.showLimitedPermissionsModal()
        return
      }
    }
    const value = cb.dataset.filterValue
    const label = cb.dataset.label || value

    const hidden = this[`selected${this.camel(type)}Target`]
    let list = hidden.value.split(",").filter(Boolean)

    if (cb.checked) {
      const isNew = !list.includes(value)
      if (isNew) {
        list.push(value)
        this.addPill(type, value, label)
      }
    } else {
      list = list.filter(v => v !== value)
      this.removePill(type, value)
    }

    hidden.value = list.join(",")
    this.toggleClearButton()
    this.submitForm()
  }

  /* ----------------------- PILLS ----------------------- */

  addPill(type, value, label) {
    const pill = document.createElement("div")
    pill.className = "filter-easy-clear-button p-2"
    pill.dataset.type = type
    pill.dataset.value = value

    pill.innerHTML = `
      ${label}
      <a href="#" class="ms-1 text-dark"
        data-action="click->ps-report-filter#removePill"
        data-type="${type}" data-value="${value}"
        aria-label="Remove filter: ${label}"
      >×</a>
    `

    this.filterPillsTarget.appendChild(pill)
    this.toggleClearButton()
  }

  removePill(eventOrType, valueIfDirect) {
    if (eventOrType instanceof Event) {
      const pillType = eventOrType.currentTarget?.dataset?.type
      if (!this.isPublicPracticeSmartFilterType(pillType)) {
        if (this.isGuestUser()) {
          eventOrType.preventDefault()
          this.showSignInRequiredModal()
          return
        }
        if (this.isLimitedAccessUser()) {
          eventOrType.preventDefault()
          this.showLimitedPermissionsModal()
          return
        }
      }
    }
    // --- Parse arguments ---
    let type, value;
    if (eventOrType instanceof Event) {
      eventOrType.preventDefault();
      const el = eventOrType.currentTarget;
      type = el.dataset.type;
      value = el.dataset.value;
    } else {
      type = eventOrType;
      value = valueIfDirect;
    }

    // --- Remove pill visually ---
    this._removePillElement(type, value);

    // --- Uncheck checkbox if present ---
    this._uncheckCheckbox(type, value);

    // --- Handle JSON-based filters (custom_course, assessment_delivery) ---
    if (type === "custom_course" || type === "assessment_delivery") {
      this._removeJsonFilter(type, value);
      this.toggleClearButton();
      this._dispatchPillRemoved(type, value);
      setTimeout(() => this.submitForm(), 50);
      return;
    }

    // --- Handle comma-separated filters (legacy types) ---
    const hidden = this[`selected${this.camel(type)}Target`];
    if (hidden) {
      let list = hidden.value.split(",").filter(Boolean);
      list = list.filter(v => v !== value);
      hidden.value = list.join(",");
    }
    this.toggleClearButton();
    this.submitForm();
  }

  // --- Helper: Remove pill element from DOM ---
  _removePillElement(type, value) {
    const pill = this.filterPillsTarget.querySelector(
      `[data-type="${type}"][data-value="${value}"]`
    );
    if (pill) pill.remove();
  }

  // --- Helper: Uncheck checkbox for filter if present ---
  _uncheckCheckbox(type, value) {
    const checkbox = document.querySelector(
      `input[data-filter-type="${type}"][data-filter-value="${value}"]`
    );
    if (checkbox) checkbox.checked = false;
  }

  // --- Helper: Remove from JSON-based hidden field ---
  _removeJsonFilter(type, value) {
    const target = type === "custom_course" ? this.selectedCustomCoursesTarget : this.selectedAssessmentDeliveriesTarget;
    if (target) {
      let obj = JSON.parse(target.value || '{}');
      delete obj[value];
      target.value = JSON.stringify(obj);
    }
  }

  // --- Helper: Dispatch pill-removed event ---
  _dispatchPillRemoved(type, value) {
    const event = new CustomEvent("ps:pill-removed", {
      detail: { type, value },
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(event);
  }

  /* ----------------------- RESTORE ON LOAD ----------------------- */

  handleFrameLoad = (event) => {
    const frame = event.target
    if (frame.id === "courses-dropdown-list") {
      this.restoreCoursePills()
    }
    this.restoreCustomCourseAndDeliveryPills();
    this.initializeDatePickers();
  }
  restoreNonCoursePills() {
    this.restorePillsFromHiddenFields(["body_systems", "clinical_disciplines"]);
    this.restoreCustomCourseAndDeliveryPills();
  }

  restoreCoursePills() {
    this.restorePillsFromHiddenFields(["courses"])
  }

  restorePillsFromHiddenFields(types) {
    const fieldMap = {
      body_systems: this.hasSelectedBodySystemsTarget
        ? this.selectedBodySystemsTarget
        : null,
      clinical_disciplines: this.hasSelectedClinicalDisciplinesTarget
        ? this.selectedClinicalDisciplinesTarget
        : null,
      courses: this.hasSelectedCoursesTarget
        ? this.selectedCoursesTarget
        : null
    };

    types.forEach(type => {
      const field = fieldMap[type];
      if (!field || !field.value) return;

      const arr = field.value.split(",").filter(Boolean);

      arr.forEach(value => {
        let label = value;
        const cb = document.querySelector(
          `input[data-filter-type="${type}"][data-filter-value="${value}"]`
        );

        if (cb) label = cb.dataset.label || value;

        this.addPill(type, value, label);
      });
    });
  }

  syncPillsWithVisibleFilters() {
    // Only visually remove pills for hidden types, do not update hidden fields or trigger events
    const deliveryVisible = !document.getElementById("delivery_request_form").classList.contains("d-none");
    const visible = {
      body_systems: !document.getElementById("body-system-filter").classList.contains("d-none"),
      clinical_disciplines: !document.getElementById("discipline-filter").classList.contains("d-none"),
      courses: !document.getElementById("courses-filter").classList.contains("d-none"),
      students: deliveryVisible,
      custom_course: deliveryVisible,
      assessment_delivery: deliveryVisible
    };

    // Remove all custom_course and assessment_delivery pills first to prevent duplicates
    this.filterPillsTarget.querySelectorAll('.custom-course-pill, .assessment-delivery-pill').forEach(pill => pill.remove());

    this.filterPillsTarget
      .querySelectorAll("[data-type]")
      .forEach(pill => {
        const type = pill.dataset.type;
        // Only visually remove the pill, do not update hidden fields or trigger events
        if (!visible[type]) {
          pill.remove();
        }
      });

    // Only restore custom_course and assessment_delivery pills if delivery_request_form is visible
    if (deliveryVisible) {
      this.restoreCustomCourseAndDeliveryPills();
    }
  }

  restoreCustomCourseAndDeliveryPills(customCoursesArg = null, assessmentDeliveriesArg = null) {
    // Only restore if delivery_request_form is visible
    const deliveryVisible = !document.getElementById("delivery_request_form").classList.contains("d-none");
    if (!deliveryVisible) return;

    // Remove old pills
    this.filterPillsTarget.querySelectorAll(".custom-course-pill, .assessment-delivery-pill").forEach(pill => pill.remove());

    // Always fetch from hidden fields by ID if targets are missing
    let customCourses = customCoursesArg;
    if (!customCourses) {
      let field = this.hasSelectedCustomCoursesTarget ? this.selectedCustomCoursesTarget : document.getElementById("selectedCustomCourses");
      try {
        customCourses = field && field.value ? JSON.parse(field.value) : {};
      } catch (e) { customCourses = {}; }
    }
    this.renderPills(customCourses, "custom_course");

    let assessmentDeliveries = assessmentDeliveriesArg;
    if (!assessmentDeliveries) {
      let field = this.hasSelectedAssessmentDeliveriesTarget ? this.selectedAssessmentDeliveriesTarget : document.getElementById("selectedAssessmentDeliveries");
      try {
        assessmentDeliveries = field && field.value ? JSON.parse(field.value) : {};
      } catch (e) { assessmentDeliveries = {}; }
    }
    this.renderPills(assessmentDeliveries, "assessment_delivery");

    this.toggleClearButton();
  }

  renderPills(items, type) {
    Object.entries(items).forEach(([id, name]) => {
      const pill = document.createElement("div");
      pill.className = `filter-easy-clear-button p-2 ${type === "custom_course" ? "custom-course-pill" : "assessment-delivery-pill"}`;
      pill.dataset.type = type;
      pill.dataset.value = id;
      pill.innerHTML = `
        ${name}
        <a href="#" class="text-black ms-1" aria-label="Remove ${name}"
          data-action="click->ps-report-filter#removePill"
          data-type="${type}" data-value="${id}">×</a>
      `;
      this.filterPillsTarget.appendChild(pill);
    });
  }

  /* ----------------------- CLEAR ALL ----------------------- */

  clearAll() {
    if (this.isGuestUser()) {
      this.showSignInRequiredModal()
      return
    }
    if (this.isLimitedAccessUser()) {
      this.showLimitedPermissionsModal()
      return
    }

    this.selectedBodySystemsTarget.value = ""
    this.selectedClinicalDisciplinesTarget.value = ""
    this.selectedCoursesTarget.value = ""
    this.selectedStudentsTarget.value = ""
    if (this.hasSelectedCustomCoursesTarget) {
      this.selectedCustomCoursesTarget.value = "{}"
    }
    if (this.hasSelectedAssessmentDeliveriesTarget) {
      this.selectedAssessmentDeliveriesTarget.value = "{}"
    }

    document.querySelectorAll("[data-filter-type]").forEach(cb => (cb.checked = false))

    this.filterPillsTarget.innerHTML = ""
    this.toggleClearButton()
    this.submitForm()
  }

  toggleClearButton() {
    const btn = document.getElementById("clear-filters-btn")
    if (btn) btn.classList.toggle("d-none", this.filterPillsTarget.children.length === 0)
  }

  /* ----------------------- TAB BEHAVIOR ----------------------- */

  handleTabClick(event) {
    const tab = event.currentTarget.dataset.tab
    this.selectedTabTarget.value = tab

    // Hide/show filters based on current tab
    this.applyTabFilterVisibility(tab)

    // Remove pills not allowed in this tab
    this.syncPillsWithVisibleFilters()

    // Update export links and save forms with new tab value
    this.updateActionParams(tab);
  }

  updateActionParams(tab) {
    // Update all export links' tab param efficiently
    ["export-csv-link", "export-xlsx-link"].forEach(id => {
      const link = document.getElementById(id);
      if (link) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set("tab", tab);
        link.href = url.toString();
      }
    });

    // Update tab in all preferences JSON fields in save forms
    ["save-report-form", "save-default-report-form"].forEach(formId => {
      const form = document.getElementById(formId);
      if (!form) return;
      const input = form.querySelector('input[name="report_view_preference[preferences]"]');
      if (!input) return;
      try {
        const data = JSON.parse(input.value);
        if (data.tab !== tab) {
          data.tab = tab;
          input.value = JSON.stringify(data);
        }
      } catch (_) {}
    });
  }

  applyTabFilterVisibility(tab) {
    const discipline = document.getElementById("discipline-filter");
    const bodySystem = document.getElementById("body-system-filter");
    const courses = document.getElementById("courses-filter");
    const students = document.getElementById("delivery_request_form");

    if (!discipline || !bodySystem || !courses || !students) return;

    if (tab === "system_view") {
      discipline.classList.add("d-none");
      bodySystem.classList.add("d-none");
      courses.classList.remove("d-none");
      students.classList.remove("d-none");
    }
    else if (tab === "discipline_view") {
      bodySystem.classList.add("d-none");
      discipline.classList.add("d-none");
      courses.classList.remove("d-none");
      students.classList.remove("d-none");
    }
    else if (tab === "most_missed_view") {
      discipline.classList.remove("d-none");
      bodySystem.classList.remove("d-none");
      courses.classList.remove("d-none");
      students.classList.remove("d-none");
    }
  }

  // SEARCH WITHIN DROPDOWNS //
  filterList(event) { 
    const query = event.target.value.toLowerCase() 
    const items = event.target.parentElement.querySelectorAll(".form-check") 
    items.forEach(item => { 
      const text = item.textContent.toLowerCase() 
      item.style.display = text.includes(query) ? "" : "none"
    }) 
  }

  // SEARCH WITIN TABLE //
  triggerSearchOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault() // prevent default form submit behavior
      this.submitForm()
    }
  }

  /* ----------------------- UTIL ----------------------- */

  camel(str) {
    return str
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join("")
  }

  handleStudentsUpdated = (event) => {
    const grduIds = event.detail.grduIds || [];

    // Update hidden field for students only
    if (this.hasSelectedStudentsTarget) {
      this.selectedStudentsTarget.value = grduIds.join(",");
    }

    // Pills will be rendered from hidden fields
    this.restoreCustomCourseAndDeliveryPills();

    // Only submit form if not triggered by pill removal
    if (!event.detail.fromPillRemoval) {
      this.submitForm();
    }
  }

  // Show a special pill when custom students are selected
  addCustomStudentFilterPill() {
    if (!this.hasFilterPillsTarget) return;
    if (this.filterPillsTarget.querySelector("#custom-student-pill")) return;

    const pill = document.createElement("div");
    pill.id = "custom-student-pill";
    pill.className = "filter-easy-clear-button p-2";
    pill.innerHTML = `
      Custom student selection
      <a href="#" class="text-black ms-1" aria-label="Remove student filter"
        data-action="click->ps-report-filter#removeCustomStudentFilter">×</a>
    `;

    this.filterPillsTarget.appendChild(pill);
    this.toggleClearButton()
  }

  // Clear custom student filter and refresh results
  removeCustomStudentFilter(event) {
    event.preventDefault();
    if (this.isGuestUser()) {
      this.showSignInRequiredModal()
      return
    }
    if (this.isLimitedAccessUser()) {
      this.showLimitedPermissionsModal()
      return
    }

    const pill = this.filterPillsTarget.querySelector("#custom-student-pill");
    if (pill) pill.remove();

    if (this.hasSelectedStudentsTarget) {
      this.selectedStudentsTarget.value = "";
    }
    this.toggleClearButton()
    this.submitForm()
  }

  loadCourses() {
    const url = this.element.dataset.coursesUrl;
    if (!url) return;

    Turbo.visit(url, { frame: "courses-dropdown-list" });
  }

  showSkeleton(event) {
    const clickedTab = event.currentTarget;

    // Remove active+disabled from all tabs
    document.querySelectorAll(".tab-link").forEach(tab => {
      tab.classList.remove("active", "disabled");
    });

    // Add active+disabled to clicked tab
    clickedTab.classList.add("active", "disabled");
    this.addSkeleton()
  }

  skeletonTemplate() {
    return `
      <div class="skeleton-table bg-white table-responsive-fixed p-4 mb-4">
        <div class="d-flex gap-3 mb-2">
          ${"<div class='skeleton-box table-header'></div>".repeat(6)}
        </div>
        ${Array.from({ length: 12 }).map((_, i) => `
          <div class="d-flex gap-3 mb-2 align-items-center ${i % 2 === 0 ? 'skeleton-row-alt' : ''} table-row">
            ${"<div class='skeleton-box table-cell'></div>".repeat(6)}
          </div>
        `).join("")}
      </div>
    `
  }

  addSkeleton(){
    // Show skeleton while content loads
    const frame = document.getElementById("practiceSmartTabsContent");
    if (frame) {
      frame.innerHTML = this.skeletonTemplate();
    }
  }

  showReportSkeleton(){
    const detailFrame = document.getElementById("system-view") ||
                        document.getElementById("most-missed-table") || 
                        document.getElementById("discipline-view");

    if (detailFrame) {
      detailFrame.innerHTML = this.skeletonTemplate();
    }
  }

  toggleCard(event) {
    const category = event.currentTarget.dataset.category
    if (!category) return

    const container = event.currentTarget.closest("turbo-frame, .container, body")

    // Hide all expanded rows
    container.querySelectorAll(".expanded-row")
            .forEach(el => el.classList.add("d-none"))

    // Show all collapsed rows
    container.querySelectorAll(".collapsed-row")
            .forEach(el => el.classList.remove("d-none"))

    // Expand only one
    const expanded = container.querySelector(`.expanded-row[data-category="${category}"]`)
    const collapsed = container.querySelector(`.collapsed-row[data-category="${category}"]`)

    if (expanded) expanded.classList.remove("d-none")
    if (collapsed) collapsed.classList.add("d-none")

    // Save selection in hidden fields for turbo refresh
    const type = event.currentTarget.dataset.filterType
    const cardName = expanded?.querySelector(".category-name")?.innerText?.trim()

    if (type === "system" && this.hasSelectedSystemCardTarget) {
      this.selectedSystemCardTarget.value = cardName
    }
    if (type === "discipline" && this.hasSelectedDisciplineCardTarget) {
      this.selectedDisciplineCardTarget.value = cardName
    }

    // Show detail skeleton
    const detailFrame =
      document.getElementById("system-detail-table") ||
      document.getElementById("discipline-detail-table");

    if (detailFrame) {
      detailFrame.innerHTML = this.skeletonTemplate();
    }
  }

  applySort(event) {
    event.preventDefault()

    const link = event.currentTarget
    const sortColumn = link.dataset.sortColumn
    const sortDirection = link.dataset.sortDirection
    const turboFrame = link.dataset.turboFrame
    const tab = link.dataset.tab

    // Read real-time values from hidden fields
    const selectedSystem = this.hasSelectedSystemCardTarget ? this.selectedSystemCardTarget.value : null
    const selectedDiscipline = this.hasSelectedDisciplineCardTarget ? this.selectedDisciplineCardTarget.value : null
    const selectedCourses = this.hasSelectedCoursesTarget ? this.selectedCoursesTarget.value : null
    const selectedStudents = this.hasSelectedStudentsTarget ? this.selectedStudentsTarget.value : null
    const selectedBodySystems = this.hasSelectedBodySystemsTarget ? this.selectedBodySystemsTarget.value : null
    const selectedClinicalDisciplines = this.hasSelectedClinicalDisciplinesTarget ? this.selectedClinicalDisciplinesTarget.value : null
    // Build params
    const params = new URLSearchParams(window.location.search)
    params.set("sort_column", sortColumn)
    params.set("sort_direction", sortDirection)
    params.set("selected_system_card", selectedSystem)
    params.set("selected_discipline_card", selectedDiscipline)
    params.set("frame_id", turboFrame)
    params.set("tab", tab)
    selectedCourses && params.set("selected_courses", selectedCourses)
    selectedStudents && params.set("selected_students", selectedStudents)
    selectedBodySystems && params.set("selected_body_systems", selectedBodySystems)
    selectedClinicalDisciplines && params.set("selected_clinical_disciplines", selectedClinicalDisciplines)
    const url = `${window.location.pathname}?${params.toString()}`

    Turbo.visit(url, { frame: turboFrame })
  }

  toggleSaveButton() {
    if (!this.hasSaveReportNameTarget || !this.hasSaveReportButtonTarget) return;
    this.saveReportButtonTarget.disabled = this.saveReportNameTarget.value.trim() === "";
  }

  focusSaveInput() {
    if (this.hasSaveReportNameTarget) {
      this.saveReportNameTarget.focus()
    }
  }

  syncTrExpandedClass() {
    document.querySelectorAll('button[data-bs-toggle="collapse"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        var tr = btn.closest('tr');
        setTimeout(() => {
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          if (tr) {
            if (isExpanded) {
              tr.classList.add('expanded');
            } else {
              tr.classList.remove('expanded');
            }
          }
        }, 350);
      });
    });
  }
}
